import { CommonModule } from '@angular/common'
import { Component, OnInit, inject, input, output, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MessageService } from '@openng/optimus-ui/api'
import { OptimUiSharedModule } from '../../shared/optimus-ui-shared.module'
import { APP_VERSION } from '../app-info'
import { ClassManagerApiService } from '../../_services/system_api/classManager/api.service'
import {
  DEFAULT_SUPPORT_CATEGORIES,
  SupportCategory,
  SupportTicket,
  SupportTicketApiService,
  SupportTicketCategory,
  SupportTicketPriority
} from '../../_services'

/**
 * Form di apertura ticket di supporto, condiviso dalla top bar (Support) e
 * dalla scheda sede (site hub). La categoria predefinita è sempre
 * "Modifica dati" per ogni nuovo ticket.
 */
@Component({
  selector: 'fq-support-ticket-form',
  standalone: true,
  imports: [OptimUiSharedModule, CommonModule, FormsModule],
  templateUrl: './support-ticket-form.component.html'
})
export class SupportTicketFormComponent implements OnInit {
  private readonly api = inject(SupportTicketApiService)
  private readonly classApi = inject(ClassManagerApiService)
  private readonly messageService = inject(MessageService)

  /** Sede preselezionata (es. site hub). */
  readonly siteKey = input<string | null>(null)
  /** Sede fissa e non modificabile (es. scheda sede). */
  readonly siteLocked = input(false)
  /** Contesto da allegare al ticket; se assente usa l'URL corrente. */
  readonly contextUrl = input<string | null>(null)
  readonly created = output<SupportTicket>()

  readonly submitting = signal(false)
  readonly categories = signal<SupportCategory[]>(DEFAULT_SUPPORT_CATEGORIES)
  readonly siteOptions = signal<{ label: string, value: string }[]>([])

  readonly priorities = [
    { label: 'Bassa', value: 'low' },
    { label: 'Normale', value: 'normal' },
    { label: 'Alta', value: 'high' }
  ]

  title = ''
  body = ''
  category: SupportTicketCategory = 'data-change'
  priority: SupportTicketPriority = 'normal'
  site = ''

  private sitesLoaded = false

  ngOnInit (): void {
    this.site = this.siteKey() ?? ''
    this.loadCategories()
    if (!this.siteLocked()) {
      this.loadSites()
    }
  }

  canSubmit (): boolean {
    return !!this.title.trim() && !this.submitting()
  }

  submit (): void {
    if (!this.canSubmit()) {
      return
    }
    this.submitting.set(true)
    this.api.create({
      title: this.title.trim(),
      body: this.body.trim(),
      category: this.category,
      priority: this.priority,
      contextUrl: (this.contextUrl() || window.location.href).slice(0, 1000),
      appVersion: APP_VERSION,
      userAgent: (navigator.userAgent || '').slice(0, 500),
      siteKey: this.site || null
    }).subscribe({
      next: (ticket) => {
        this.submitting.set(false)
        this.title = ''
        this.body = ''
        this.category = 'data-change'
        this.priority = 'normal'
        this.site = this.siteKey() ?? ''
        this.messageService.add({ severity: 'success', summary: 'Supporto', detail: 'Segnalazione inviata' })
        this.created.emit(ticket)
      },
      error: (error) => {
        this.submitting.set(false)
        if (!error.isintercepted) {
          this.messageService.add({ severity: 'error', summary: 'Supporto', detail: error?.error?.message ?? 'Errore durante l\'invio' })
        }
      }
    })
  }

  private loadCategories (): void {
    this.api.getCategories().subscribe({
      next: (data) => {
        if (!Array.isArray(data) || data.length === 0) {
          return
        }
        this.categories.set(data)
        if (!data.some((category) => category.code === this.category)) {
          this.category = data.some((category) => category.code === 'data-change')
            ? 'data-change'
            : data[0].code
        }
      },
      error: () => { /* resta il fallback DEFAULT_SUPPORT_CATEGORIES */ }
    })
  }

  private loadSites (): void {
    if (this.sitesLoaded) {
      return
    }
    this.sitesLoaded = true
    this.classApi.getAnagraficaSiti().subscribe({
      next: (rows) => {
        const options = (Array.isArray(rows) ? rows : [])
          .filter((row) => row && row.fraz)
          .map((row) => ({
            label: `${row.fraz}${row.denominazione ? ' — ' + row.denominazione : ''}`,
            value: String(row.fraz)
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        this.siteOptions.set(options)
      },
      error: () => {
        this.sitesLoaded = false
      }
    })
  }
}
